import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MessageSquare, ShieldCheck } from 'lucide-react';
import api from '@/lib/axios';
import convApi, { setCsrf } from '@/lib/conversations';

const REUSE_MS = 24 * 60 * 60 * 1000;
const empty = {
  primeiro_nome: '', ultimo_nome: '', email: '', telefone: '', mensagem: '',
  code: '', phone_code: '', verification_token: '', phone_verification_token: '',
  aceita_termos: false, aceita_privacidade: false,
};

// Bloco 1.º contacto (Fase 4): verificação única de email e abertura de conversa privada.
export default function ConversationStartForm({ listingId, titulo, recurso = 'anuncio', assunto = '' }) {
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [emailState, setEmailState] = useState({ sending: false, sent: false, verified: false, err: '' });
  const [phoneState, setPhoneState] = useState({ sending: false, sent: false, verified: false, err: '' });
  const [state, setState] = useState({ loading: false, err: '' });
  const [confirmShare, setConfirmShare] = useState(null);

  const set = (patch) => setForm((p) => ({ ...p, ...patch }));

  // start_conversation: início real do formulário de contacto (uma vez por montagem)
  useEffect(() => {
    let cancelled = false;
    import('@/lib/analytics').then(({ track }) => { if (!cancelled) track('start_conversation', { recurso, listing: !!listingId }); }).catch(() => {});
    return () => { cancelled = true; };
  }, [recurso, listingId]);

  useEffect(() => {
    const email = form.email.trim().toLowerCase();
    if (!email || form.verification_token) return;
    try {
      const saved = JSON.parse(localStorage.getItem('mp_lead_verify') || 'null');
      if (saved && saved.email === email && saved.token && Date.now() - saved.at < REUSE_MS) {
        set({ verification_token: saved.token });
        setEmailState((s) => ({ ...s, verified: true }));
      }
    } catch (_) {}
  }, [form.email, form.verification_token]);

  const sendEmailCode = async () => {
    if (!form.email.trim()) { setEmailState((s) => ({ ...s, err: 'Preencha o email.' })); return; }
    setEmailState({ sending: true, sent: false, verified: false, err: '' });
    try {
      await api.post('/marketplace/verificacao/enviar-codigo', { email: form.email.trim() });
      setEmailState({ sending: false, sent: true, verified: false, err: '' });
    } catch (e) {
      setEmailState({ sending: false, sent: false, verified: false, err: e.response?.data?.detail || 'Falha ao enviar o código.' });
    }
  };

  const verifyEmail = async () => {
    setEmailState((s) => ({ ...s, err: '' }));
    try {
      const res = await api.post('/marketplace/verificacao/confirmar', { email: form.email.trim(), code: form.code.trim() });
      set({ verification_token: res.data.verification_token });
      setEmailState((s) => ({ ...s, verified: true }));
      try {
        localStorage.setItem('mp_lead_verify', JSON.stringify({
          email: form.email.trim().toLowerCase(), token: res.data.verification_token, at: Date.now(),
        }));
      } catch (_) {}
    } catch (e) {
      setEmailState((s) => ({ ...s, verified: false, err: e.response?.data?.detail || 'Código inválido.' }));
    }
  };

  const sendSms = async () => {
    setPhoneState({ sending: true, sent: false, verified: false, err: '' });
    try {
      await api.post('/marketplace/verificacao/enviar-sms', { telefone: form.telefone.trim() });
      setPhoneState({ sending: false, sent: true, verified: false, err: '' });
    } catch (e) {
      setPhoneState({ sending: false, sent: false, verified: false, err: e.response?.data?.detail || 'Falha ao enviar o SMS.' });
    }
  };

  const verifySms = async () => {
    try {
      const res = await api.post('/marketplace/verificacao/confirmar-sms', { telefone: form.telefone.trim(), code: form.phone_code.trim() });
      set({ phone_verification_token: res.data.verification_token });
      setPhoneState((s) => ({ ...s, verified: true, err: '' }));
    } catch (e) {
      setPhoneState((s) => ({ ...s, verified: false, err: e.response?.data?.detail || 'Código SMS inválido.' }));
    }
  };

  const submit = async (event, confirmarPartilha = false) => {
    if (event) event.preventDefault();
    if (!form.verification_token) { setState({ loading: false, err: 'Verifique o seu email.' }); return; }
    if (form.telefone.trim() && !form.phone_verification_token) {
      setState({ loading: false, err: 'Indicou telemóvel — verifique-o por SMS ou deixe vazio.' }); return;
    }
    if (!form.aceita_termos) { setState({ loading: false, err: 'Aceite os Termos das conversas.' }); return; }
    if (!form.aceita_privacidade) { setState({ loading: false, err: 'Confirme que leu a Política de Privacidade.' }); return; }
    setState({ loading: true, err: '' });
    try {
      const res = await convApi.post('/iniciar', {
        listing_id: listingId,
        primeiro_nome: form.primeiro_nome.trim(),
        ultimo_nome: form.ultimo_nome.trim(),
        email: form.email.trim(),
        telefone: form.telefone.trim(),
        mensagem: form.mensagem.trim(),
        aceita_termos: form.aceita_termos,
        aceita_privacidade: form.aceita_privacidade,
        email_verification_token: form.verification_token,
        phone_verification_token: form.phone_verification_token,
        confirmar_partilha: confirmarPartilha,
        recurso,
        assunto,
      });
      if (res.data?.requer_confirmacao) {
        setState({ loading: false, err: '' });
        setConfirmShare(res.data.aviso);
        return;
      }
      setCsrf(res.data.csrf);
      navigate(`/conversas/${res.data.conversation_id}`);
    } catch (e) {
      setState({ loading: false, err: e.response?.data?.detail || 'Não foi possível abrir a conversa.' });
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-3" data-testid="conversation-start-form">
      <p className="inline-flex items-center gap-2 text-xs font-semibold text-emerald-700">
        <MessageSquare className="h-4 w-4" /> Mensagem privada dentro do Marketplace
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <input className="rounded-xl border border-slate-300 px-3 py-2" placeholder="Primeiro nome"
          value={form.primeiro_nome} onChange={(e) => set({ primeiro_nome: e.target.value })}
          data-testid="conv-first-name" required />
        <input className="rounded-xl border border-slate-300 px-3 py-2" placeholder="Último nome"
          value={form.ultimo_nome} onChange={(e) => set({ ultimo_nome: e.target.value })}
          data-testid="conv-last-name" required />
      </div>
      <p className="text-[11px] text-slate-500">
        O seu nome completo só é visível dentro desta conversa. Nas páginas públicas mostramos apenas nomes públicos.
      </p>
      <input className="rounded-xl border border-slate-300 px-3 py-2" type="email" placeholder="Email"
        value={form.email} onChange={(e) => set({ email: e.target.value })} data-testid="conv-email" required />

      <div className="space-y-2 rounded-xl border border-dashed border-emerald-300 bg-emerald-50/60 p-3">
        <p className="text-xs font-semibold text-slate-600">1) Verifique o seu email (uma única vez)</p>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={sendEmailCode} disabled={emailState.sending || emailState.verified}
            className="rounded-lg border border-emerald-300 px-3 py-2 text-xs font-semibold text-emerald-700 disabled:opacity-60"
            data-testid="conv-send-email-code">
            {emailState.verified ? '✓ Email verificado' : (emailState.sending ? 'A enviar...' : (emailState.sent ? 'Reenviar código' : 'Enviar código'))}
          </button>
          {!emailState.verified && (
            <>
              <input className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Código de 6 dígitos"
                value={form.code} onChange={(e) => set({ code: e.target.value })} data-testid="conv-email-code" />
              <button type="button" onClick={verifyEmail}
                className="rounded-lg border border-emerald-300 px-3 py-2 text-xs font-semibold text-emerald-700"
                data-testid="conv-verify-email">Validar</button>
            </>
          )}
        </div>
        {emailState.err && <p className="text-xs text-red-600" data-testid="conv-email-error">{emailState.err}</p>}
      </div>

      <input className="rounded-xl border border-slate-300 px-3 py-2" placeholder="Telemóvel (opcional)"
        value={form.telefone} onChange={(e) => set({ telefone: e.target.value })} data-testid="conv-phone" />
      {form.telefone.trim() && (
        <div className="space-y-2 rounded-xl border border-dashed border-cyan-300 bg-cyan-50/60 p-3" data-testid="conv-sms-block">
          <p className="text-xs font-semibold text-slate-600">2) Verifique o telemóvel (opcional)</p>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={sendSms} disabled={phoneState.sending || phoneState.verified}
              className="rounded-lg border border-cyan-300 px-3 py-2 text-xs font-semibold text-cyan-700 disabled:opacity-60"
              data-testid="conv-send-sms">{phoneState.verified ? '✓ Telefone verificado' : (phoneState.sending ? 'A enviar...' : 'Enviar SMS')}</button>
            {!phoneState.verified && (
              <>
                <input className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Código SMS"
                  value={form.phone_code} onChange={(e) => set({ phone_code: e.target.value })} data-testid="conv-sms-code" />
                <button type="button" onClick={verifySms}
                  className="rounded-lg border border-cyan-300 px-3 py-2 text-xs font-semibold text-cyan-700"
                  data-testid="conv-verify-sms">Validar</button>
              </>
            )}
          </div>
          {phoneState.err && <p className="text-xs text-red-600" data-testid="conv-sms-error">{phoneState.err}</p>}
        </div>
      )}

      <textarea className="rounded-xl border border-slate-300 px-3 py-2" rows={4}
        placeholder={titulo ? `Mensagem sobre "${titulo}"` : 'Mensagem'}
        value={form.mensagem} onChange={(e) => set({ mensagem: e.target.value })} data-testid="conv-message" required />

      <label className="flex items-start gap-2 text-xs text-slate-600" data-testid="conv-terms">
        <input type="checkbox" className="mt-0.5" checked={form.aceita_termos}
          onChange={(e) => set({ aceita_termos: e.target.checked })} data-testid="conv-terms-checkbox" />
        <span>Aceito os <Link to="/termos" target="_blank" className="font-semibold text-emerald-600 underline">Termos</Link> aplicáveis à utilização das conversas.</span>
      </label>
      <label className="flex items-start gap-2 text-xs text-slate-600" data-testid="conv-privacy">
        <input type="checkbox" className="mt-0.5" checked={form.aceita_privacidade}
          onChange={(e) => set({ aceita_privacidade: e.target.checked })} data-testid="conv-privacy-checkbox" />
        <span>Li a <Link to="/privacidade" target="_blank" className="font-semibold text-emerald-600 underline">Política de Privacidade</Link> (mensagens guardadas, moderação e conservação).</span>
      </label>

      {confirmShare && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800" data-testid="conv-share-confirm">
          <p className="font-semibold">{confirmShare}</p>
          <div className="mt-2 flex gap-2">
            <button type="button" onClick={() => submit(null, true)}
              className="rounded-lg bg-amber-600 px-3 py-1.5 font-semibold text-white" data-testid="conv-share-confirm-yes">Confirmar e enviar</button>
            <button type="button" onClick={() => setConfirmShare(null)}
              className="rounded-lg border border-amber-300 px-3 py-1.5 font-semibold" data-testid="conv-share-confirm-no">Voltar e editar</button>
          </div>
        </div>
      )}

      <button type="submit" disabled={state.loading}
        className="rounded-xl bg-emerald-600 px-4 py-2.5 font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-60"
        data-testid="conv-submit">{state.loading ? 'A abrir conversa...' : 'Enviar mensagem'}</button>
      {state.err && <p className="text-sm font-semibold text-red-600" data-testid="conv-error">{state.err}</p>}
      <p className="inline-flex items-start gap-1.5 text-[11px] text-slate-500">
        <ShieldCheck className="mt-0.5 h-3.5 w-3.5 text-emerald-600" />
        Sem contas nem passwords: confirmamos o seu email e recebe uma ligação segura para continuar a conversa.
      </p>
    </form>
  );
}
